import { AfterViewInit, Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SystemEvent, SystemEventDismissalService, SystemEventsHubService, SystemEventsRepositoryService, Utilities } from 'reg-hub-common';
import { HttpParams } from '@angular/common/http';
import {
  trigger,
  style,
  transition,
  animate,
} from '@angular/animations';
import { interval } from 'rxjs';

@Component({
    selector: 'app-system-event-notification',
    templateUrl: './system-event-notification.component.html',
    styleUrls: ['./system-event-notification.component.css'],
    animations: [
        trigger('expandHeight', [
            transition(':enter', [
                style({ height: '0', opacity: 0 }),
                animate('250ms ease-in-out', style({ height: '*', opacity: 1 })),
            ]),
            transition(':leave', [
                animate('250ms ease-in-out', style({ height: '0', opacity: 0 })),
            ]),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SystemEventNotificationComponent implements AfterViewInit {
  protected _events: SystemEvent[] = [];
  protected _eventMessage: string | null = null;
  protected _eventID: string | undefined;
  protected _isNotice: boolean = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    protected repo: SystemEventsRepositoryService,
    protected hub: SystemEventsHubService,
    protected dismissals: SystemEventDismissalService) { }

  ngAfterViewInit(): void {
    this.repo.getFirstPageSubscribable(new HttpParams()
      .append('isDisplayed', true)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(events => {
        this.setEvents(events);
      });

    this.hub.connect();
    this.hub.events$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(events => {
      this.setEvents(events);
    });

    //checking in memory events periodically for expired ones (setEvents also refreshes the notice/warning style)
    interval(10000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this._events.length > 0) {
        const stillOpen = this._events.filter(event => Utilities.isSystemEventDisplayed(event));

        // Re-run through setEvents so dismissals and the derived banner fields stay in sync.
        this.setEvents(stillOpen);
      }
    })
  }

  ngOnDestroy(): void {
    this.hub.disconnect();
  }

  // Dismiss just for this session — the event returns on reload or the next push.
  dismissForNow() {
    if (!this._eventID) {
      return;
    }

    this.setEvents(this._events.filter(event => event.id !== this._eventID));
  }

  // Dismiss for a day — persisted in localStorage so it survives reloads and pushes.
  dismissForOneDay() {
    if (!this._eventID) {
      return;
    }

    this.dismissals.dismiss(this._eventID);
    // Re-filter the current list so the dismissed event drops out and the next one surfaces.
    this.setEvents(this._events);
  }

  private setEvents(events: SystemEvent[]) {
    this._events = this.dismissals.filterDismissed(events ?? []);

    const event = this._events.length > 0 ? this._events[0] : null;
    this._eventMessage = event?.message ?? null;
    this._eventID = event?.id;
    // Blue notice before a scheduled outage starts, red warning once the service is down.
    this._isNotice = event ? Utilities.isSystemEventNotice(event) : false;
  }
}